/**
 * Returns a singleton created once and reused across Vite HMR re-executions of
 * the calling module — in dev only. Outside `import.meta.hot` (production
 * builds) it is a plain passthrough to `create()`.
 *
 * Vite HMR re-runs a changed module's whole import graph (each import gets a
 * fresh `?t=` query), so a module-level `const x = new Stateful()` /
 * `signal(...)` would otherwise be rebuilt on every edit elsewhere — orphaning
 * the loaded instance, leaking timers, and resetting state that live components
 * still reference (see `@pentacode/apps-common/vite/README.md`). Keyed storage
 * on `globalThis` survives the re-execution.
 *
 * Production runs each module exactly once, so the passthrough is equivalent —
 * and it keeps page-lifetime state off `globalThis`, which matters when the app
 * is embedded in a partner host page (PTC-3919).
 */
export function persistent<T>(key: string | symbol, create: () => T): T {
    if (!import.meta.hot) {
        return create();
    }

    const store: Record<PropertyKey, unknown> = ((globalThis as Record<string, unknown>).__ptcSingletons ??=
        {}) as Record<PropertyKey, unknown>;

    if (!(key in store)) {
        store[key] = create();
    }
    return store[key] as T;
}
